import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { coreApi } from '@/integrations/api/active';
import { supabase } from '@/integrations/supabase/client';
import { useSessionStore } from '@/modules/auth/session-store';
import { resetStateForAccountChange } from '@/modules/auth/account-state';
import type { User, Session } from '@supabase/supabase-js';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  session: null,
  loading: true,
  signOut: async () => {},
});

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const ensureTimezone = async (u: User) => {
      try {
        const { data } = await supabase.from('profiles').select('timezone').eq('id', u.id).maybeSingle();
        if (data && !(data as any).timezone) {
          const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
          if (tz) await coreApi('/profile', { timezone: tz }, 'PATCH');
        }
      } catch (e) {
        console.warn('[AuthContext] ensureTimezone failed', e);
      }
    };

    const handleSession = (sess: Session | null) => {
      // Zustand stores are module singletons: they survive sign-out, and the
      // account switcher changes user without a reload. So a second user landing
      // on a workspace they can also reach inherits the first user's cached
      // `myRole` until their own fetch resolves — long enough to flash admin-only
      // UI, which is how the setup wizard appeared to a plain member.
      //
      // Keyed on the id the data was RESOLVED for, not on a "did this fire"
      // flag: a token refresh re-runs this with the same user and must not
      // discard a warm store, which is what the synchronous wizard decision
      // depends on to avoid a loading frame for returning users.
      //
      // EVERY store that caches fetched rows, not just the two that were
      // flashing (#142). `operating-store` is the one that matters most: it holds the
      // domain content, so during the window a second user sees the first
      // account's rocks, to-dos and issues — INCLUDING content from teams they
      // have no grant on, since those rows were returned under someone else's
      // RLS context. That is not an RLS failure and RLS cannot fix it. The
      // database answered both requests correctly; the client kept the first
      // answer.
      //
      // `resetDomain` rather than a full reset, because `workspaceId`,
      // `rootTeamId` and `currentTeamId` describe where the app is pointed
      // rather than what it holds — see the store.
      resetStateForAccountChange(sess?.user?.id ?? null);

      setSession(sess);
      setUser(sess?.user ?? null);
      setLoading(false);

      if (sess?.user) {
        // Keep session store in sync
        useSessionStore.getState().addSession({
          user_id: sess.user.id,
          email: sess.user.email ?? '',
          access_token: sess.access_token,
          refresh_token: sess.refresh_token,
        });

        setTimeout(() => ensureTimezone(sess.user), 0);
      }
    };

    // The shared client's own onAuthStateChange listener fires immediately on registration,
    // reflecting only its internal (possibly stale or mid-refresh) session — racing ahead of
    // the orchestrated check below can briefly report user=null and bounce protected routes
    // to /login. Let the orchestrated refreshAllSessions/getSession chain own the first
    // resolution; the listener only drives updates after that.
    let initialResolved = false;

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, sess) => {
      if (!initialResolved) return;
      handleSession(sess);
    });

    const resolveInitialSession = () =>
      supabase.auth.getSession().then(({ data: { session: sess } }) => {
        initialResolved = true;
        handleSession(sess);
      });

    const store = useSessionStore.getState();
    const refreshOthers = () => store.refreshAllSessions().catch(() => {});
    if (/[#&]access_token=/.test(window.location.hash)) {
      resolveInitialSession().then(refreshOthers, refreshOthers);
    } else {
      store.refreshAllSessions().then(resolveInitialSession, resolveInitialSession);
    }

    return () => subscription.unsubscribe();
  }, []);

  const signOut = async () => {
    const currentUserId = user?.id;
    await supabase.auth.signOut();
    if (currentUserId) {
      const store = useSessionStore.getState();
      store.removeSession(currentUserId);
      // If other sessions remain, switch to one
      const next = store.sessions.find((s) => !s.stale);
      if (next) {
        await store.setActive(next.user_id);
      } else {
        store.removeAll();
      }
    }
  };

  return (
    <AuthContext.Provider value={{ user, session, loading, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
